import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Stack,
  Table,
  ActionIcon,
  Divider,
  Alert,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconEdit,
  IconCheck,
  IconX,
  IconFileText,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useRequisition } from "../hooks/useRequisitions";
import {
  useSubmitRequisitionForApproval,
  useApproveRequisition,
  useRejectRequisition,
  useConvertToRFQ,
} from "../hooks/useRequisitions";
import { useAuth } from "../../../auth/AuthContext";
import dayjs from "dayjs";
import { useState } from "react";
import { Textarea, Modal } from "@mantine/core";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  PENDING_APPROVAL: "yellow",
  APPROVED: "green",
  REJECTED: "red",
  CONVERTED_TO_RFQ: "blue",
};

const priorityColors: Record<string, string> = {
  LOW: "gray",
  MEDIUM: "blue",
  HIGH: "orange",
  URGENT: "red",
};

export function RequisitionDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: requisition, isLoading } = useRequisition(id);
  const submitForApproval = useSubmitRequisitionForApproval();
  const approve = useApproveRequisition();
  const reject = useRejectRequisition();
  const convertToRFQ = useConvertToRFQ();

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [comments, setComments] = useState("");

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading requisition...</Text>
      </Container>
    );
  }

  if (!requisition) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red">Requisition not found</Alert>
      </Container>
    );
  }

  const canEdit = requisition.status === "DRAFT" && requisition.requestedBy.id === user?.id;
  const canSubmit = requisition.status === "DRAFT" && requisition.requestedBy.id === user?.id;
  const canApprove =
    (user?.role === "HEAD_OF_PROCUREMENT" || user?.role === "ADMIN") &&
    requisition.status === "PENDING_APPROVAL";
  const canConvert = requisition.status === "APPROVED" && !requisition.rfq;

  const handleSubmitForApproval = () => {
    submitForApproval.mutate(requisition.id);
  };

  const handleApprove = () => {
    approve.mutate(
      { id: requisition.id, comments: comments || undefined },
      {
        onSuccess: () => {
          setApproveModalOpen(false);
          setComments("");
        },
      }
    );
  };

  const handleReject = () => {
    if (!comments.trim()) {
      return;
    }
    reject.mutate(
      { id: requisition.id, comments },
      {
        onSuccess: () => {
          setRejectModalOpen(false);
          setComments("");
        },
      }
    );
  };

  const handleConvertToRFQ = () => {
    convertToRFQ.mutate(requisition.id, {
      onSuccess: (data) => {
        navigate(`/rfq/new?requisitionId=${requisition.id}`);
      },
    });
  };

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate("/requisitions")}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>{requisition.title}</Title>
      </Group>

      <Stack gap="md">
        <Paper shadow="sm" p="md">
          <Group justify="space-between" mb="md">
            <Group>
              <Text fw={500}>Requisition Number:</Text>
              <Text>{requisition.requisitionNumber}</Text>
            </Group>
            <Group>
              <Badge color={statusColors[requisition.status]} variant="light">
                {requisition.status.replace("_", " ")}
              </Badge>
              <Badge color={priorityColors[requisition.priority]} variant="light">
                {requisition.priority}
              </Badge>
            </Group>
          </Group>

          <Divider my="md" />

          <Stack gap="sm">
            <Group>
              <Text fw={500} w={150}>
                Category:
              </Text>
              <Badge variant="light">{requisition.category}</Badge>
            </Group>
            <Group>
              <Text fw={500} w={150}>
                Department:
              </Text>
              <Text>{requisition.department}</Text>
            </Group>
            <Group>
              <Text fw={500} w={150}>
                Description:
              </Text>
              <Text style={{ flex: 1 }}>{requisition.description}</Text>
            </Group>
            <Group>
              <Text fw={500} w={150}>
                Requested By:
              </Text>
              <Text>{requisition.requestedBy.fullName}</Text>
            </Group>
            {requisition.approvedBy && (
              <Group>
                <Text fw={500} w={150}>
                  Approved By:
                </Text>
                <Text>{requisition.approvedBy.fullName}</Text>
              </Group>
            )}
            {requisition.approvedAt && (
              <Group>
                <Text fw={500} w={150}>
                  Approved At:
                </Text>
                <Text>{dayjs(requisition.approvedAt).format("DD MMM YYYY HH:mm")}</Text>
              </Group>
            )}
            {requisition.approvalComments && (
              <Group>
                <Text fw={500} w={150}>
                  Approval Comments:
                </Text>
                <Text style={{ flex: 1 }}>{requisition.approvalComments}</Text>
              </Group>
            )}
            {requisition.rfq && (
              <Group>
                <Text fw={500} w={150}>
                  RFQ:
                </Text>
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => navigate(`/rfq/${requisition.rfq?.id}`)}
                >
                  {requisition.rfq.rfqNumber}
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md">
          <Title order={4} mb="md">
            Items
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Item Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Quantity</Table.Th>
                <Table.Th>Unit</Table.Th>
                <Table.Th>Estimated Price</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {requisition.items.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.itemName}</Table.Td>
                  <Table.Td>{item.description || "-"}</Table.Td>
                  <Table.Td>{item.quantity}</Table.Td>
                  <Table.Td>{item.unit}</Table.Td>
                  <Table.Td>
                    {item.estimatedPrice
                      ? `MVR ${item.estimatedPrice.toFixed(2)}`
                      : "-"}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        <Group justify="flex-end">
          {canEdit && (
            <Button
              variant="light"
              leftSection={<IconEdit size={16} />}
              onClick={() => navigate(`/requisitions/${requisition.id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canSubmit && (
            <Button
              leftSection={<IconFileText size={16} />}
              onClick={handleSubmitForApproval}
              loading={submitForApproval.isPending}
            >
              Submit for Approval
            </Button>
          )}
          {canApprove && (
            <>
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={() => setApproveModalOpen(true)}
              >
                Approve
              </Button>
              <Button
                color="red"
                leftSection={<IconX size={16} />}
                onClick={() => setRejectModalOpen(true)}
              >
                Reject
              </Button>
            </>
          )}
          {canConvert && (
            <Button
              color="blue"
              leftSection={<IconFileText size={16} />}
              onClick={handleConvertToRFQ}
              loading={convertToRFQ.isPending}
            >
              Convert to RFQ
            </Button>
          )}
        </Group>
      </Stack>

      <Modal
        opened={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        title="Approve Requisition"
      >
        <Stack>
          <Textarea
            label="Comments (Optional)"
            placeholder="Enter approval comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setApproveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              loading={approve.isPending}
              color="green"
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Requisition"
      >
        <Stack>
          <Textarea
            label="Rejection Comments"
            placeholder="Enter rejection reason (required)"
            required
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              loading={reject.isPending}
              color="red"
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

